import PropTypes from "prop-types";
import React, { forwardRef, useRef, useState } from "react";
import styled from "styled-components";

import useInputFocus from "../../hooks/useInputFocus";
import { useUIContext } from "../../providers/UIContextProvider";
import theme from "../../theme/theme.settings";
import { TextSmall } from "../Text";
import { FieldContainer, InputContainer, InputField, InputLabel } from "./InputComponents";

const InputPassword = forwardRef((props, ref) => {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [allowPwdToggle, setAllowPwdToggle] = useState(false);
  const [passwordFieldHasBeenFocused, setPasswordFieldHasBeenFocused] = useState(false);
  const wrapper = useRef(null);
  const changeVisiblePasswordState = () => {
    setVisiblePassword(!visiblePassword);
    ref.current.focus();
  };
  const { t } = useUIContext();

  const { label, id, isError, isValid, disabled, value, onBlur, onFocus } = props;
  const [focused, onInputBlur, onInputFocus] = useInputFocus();

  // eslint-disable-next-line no-unused-vars
  const has1PasswordAutofill = () =>
    value &&
    ref.current.dataset &&
    typeof ref.current.dataset["com.agilebits.onepassword.initialValue"] !== "undefined";

  // check lastpassword autofill
  // LastPass sets base64 data as background-image for the lock icon inside input
  const hasLastPassAutofill = () =>
    ref.current &&
    ref.current.style &&
    ref.current.style.backgroundImage &&
    ref.current.style.backgroundImage.indexOf("url(") > -1 &&
    ref.current.autocomplete === "off";
  //  && !hasWebkitAutofill()
  const pwdLengthOverThreshold = () =>
    (value && value.length >= 1) || (ref && ref.current && ref.current.value.length >= 1);
  const pwdToggleAllowed = () => pwdLengthOverThreshold() && !has1PasswordAutofill() && !hasLastPassAutofill();
  const handleChange = () => {
    setAllowPwdToggle(pwdToggleAllowed());
    if (props.onChange) props.onChange();
  };
  const handleFocus = (e) => {
    setPasswordFieldHasBeenFocused(true);
    if (onFocus) onFocus(e);
  };
  const handleBlur = (e) => {
    setPasswordFieldHasBeenFocused(false);
    if (onBlur) onBlur(e);
  };

  return (
    <InputContainer>
      {label && (
        <InputLabel htmlFor={id} isValid={isValid} disabled={disabled}>
          {label}
        </InputLabel>
      )}
      <FieldContainer
        ref={wrapper}
        isError={isError}
        passwordFieldHasBeenFocused={passwordFieldHasBeenFocused}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        focused={focused}
      >
        <PasswordInputField
          {...props}
          onFocus={handleFocus}
          onBlur={handleBlur}
          type={visiblePassword ? "text" : "password"}
          ref={ref}
          onChange={handleChange}
        />
        {allowPwdToggle && (
          <ShowPasswordToggler
            role="presentation"
            className="darken"
            onClick={changeVisiblePasswordState}
            title={visiblePassword ? t("passwordInput.hidePassword") : t("passwordInput.showPassword")}
          >
            {visiblePassword ? t("passwordInput.hide") : t("passwordInput.show")}
          </ShowPasswordToggler>
        )}
      </FieldContainer>
    </InputContainer>
  );
});

InputPassword.defaultProps = {
  disabled: false,
  label: null,
  isValid: true,
  isError: false,
  onKeyPress: null,
  value: undefined,
  onChange: null,
  onBlur: null,
  onFocus: null,
  onInput: null,
};

InputPassword.propTypes = {
  disabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onKeyPress: PropTypes.func,
  label: PropTypes.string,
  isValid: PropTypes.bool,
  isError: PropTypes.bool,
  value: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onInput: PropTypes.func,
};

export default InputPassword;

// STYLED COMPONENT
// =-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-

const PasswordInputField = styled(InputField)`
  padding-right: 65px;
`;

const ShowPasswordToggler = styled.div`
  ${TextSmall}
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 10px 14px 10px 5px;
  margin-right: 4px;
  margin-left: -5px;
  user-select: none;
  color: ${theme.mediumGray};
  cursor: pointer;
`;
